// External imports
import { useQuery } from 'react-query';
import { AxiosError } from 'axios';

// Internal imports
import axios from 'helpers/axios/axios';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import config from 'Config';
import { useGetAccessToken } from 'hooks/useGetAcessToken/useGetAccessToken';
import { REQUEST_HEADERS } from '../../constants';
import { PaymentMethodResponse, createPaymentMethodResult } from './types';

/**
 * Helper to extract error message from AxiosError if status is 4xx
 */
function getDisplayError(error: unknown): { errorMessage?: string } | undefined {
  if (!error) return undefined;
  const axiosError = error as AxiosError;
  const status = axiosError?.response?.status;
  if (status && status.toString().startsWith('4')) {
    return axiosError?.response?.data as { errorMessage?: string };
  }
  return undefined;
}

/**
 * Helper to extract payment method type from response or error
 */
function getPaymentMethodType(
  isSuccess: boolean,
  data: PaymentMethodResponse | undefined,
  isError: boolean,
  error: unknown
): string | undefined {
  if (isSuccess && data?.paymentMethodType) {
    return data.paymentMethodType;
  }
  if (isError) {
    return ((error as AxiosError)?.response?.data as { paymentMethodType?: string })?.paymentMethodType;
  }
  return undefined;
}

/**
 * Custom hook to create a payment method
 */
export const usePaymentMethodCreation = (
  confirmationTokenId: string,
  region: string,
  promoCode?: string
): createPaymentMethodResult => {
  // Auth state
  const { isLoading: isAuth0Loading, appState } = useAuth0();
  const { accessToken } = useGetAccessToken();

  // Axios request options
  const options = {
    headers: {
      ...REQUEST_HEADERS,
      Authorization: `Bearer ${accessToken}`
    }
  };

  // Query for payment method creation
  const {
    isLoading: isSetupIntentLoading,
    isSuccess,
    data,
    error,
    isError
  } = useQuery(
    ['setupIntent', confirmationTokenId],
    async () => {
      const response = await axios.post<PaymentMethodResponse>(
        config.API_PAYMENT_METHOD_ENDPOINT,
        {
          confirmationTokenId,
          promoCode,
          region
        },
        options
      );
      return response.data;
    },
    {
      enabled: Boolean(appState && accessToken && confirmationTokenId),
      retry: false
    }
  );

  // Error and type extraction
  const displayError = getDisplayError(error);
  const paymentMethodType = getPaymentMethodType(isSuccess, data, isError, error);

  return {
    isLoading: isAuth0Loading || isSetupIntentLoading,
    isSuccess,
    paymentMethodType,
    paymentMethodId: data?.paymentMethodId,
    clientSecret: data?.clientSecret,
    isError,
    errorMessage: displayError?.errorMessage
  };
};